/**
 * Lot AI [27] : créneaux proposés par le choix de l'heure (`TimeSelect.vue`) à la place du champ « heure » du
 * navigateur : liste de « HH:MM » de `from` à `to` (compris) par pas de `step` minutes, plus l'heure actuelle si
 * elle tombe entre deux créneaux (une heure posée ailleurs n'est jamais perdue). Logique pure, testée sans
 * navigateur.
 */
export interface TimeSlotOption {
  value: string;
  /** « 9 h 00 » */
  label: string;
}

const toMinutes = (time: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!match) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return minutes >= 0 && minutes < 24 * 60 ? minutes : null;
};
const toTime = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** « 09:30 » → « 9 h 30 ». */
export function timeLabel(time: string): string {
  const minutes = toMinutes(time);
  if (minutes === null) return time;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
}

export function timeSlots(
  options: { step?: number; from?: string; to?: string; current?: string | null } = {},
): TimeSlotOption[] {
  const step = Math.max(5, Math.round(options.step ?? 15));
  const from = toMinutes(options.from ?? '07:00') ?? 7 * 60;
  const to = toMinutes(options.to ?? '20:00') ?? 20 * 60;
  const values = new Set<number>();
  for (let m = from; m <= to; m += step) values.add(m);
  const current = options.current ? toMinutes(options.current) : null;
  if (current !== null) values.add(current);
  return [...values].sort((a, b) => a - b).map((m) => ({ value: toTime(m), label: timeLabel(toTime(m)) }));
}
